import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDateAdapter, NgbDateParserFormatter, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SharedConstants } from '@constants';
import { FormatDatePipe } from '@pipes';
import { SasButtonComponent } from '../../sas-button/sas-button.component';
import { SasDynamicFormService } from '../sas-dynamic-form.service';
import { CustomDateAdapterService, CustomDateParserFormatterService, FormSubmittedService } from '@services';

@Component({
    selector: 'sas-datepicker-field-type',
    standalone: true,
    imports: [CommonModule, NgbDatepickerModule, TranslateModule, ReactiveFormsModule, FormlyModule, SasButtonComponent],
    providers: [
        { provide: NgbDateAdapter, useClass: CustomDateAdapterService },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatterService },
        FormatDatePipe
    ],
    styleUrl: './sas-datepicker-field-type.component.scss',
    template: `
        <div class="form-group">
        @if (field.props.label?.length){
        <label [for]="instanceId"
               [id]="field.id + 'label'"
               title="{{field.props.label ?? '' | translate}}"
               [textContent]="field.props.label"
               translate="{{field.props.label}}"
               class="form-label form-date-label">
        </label>
        @if (field.props.required) {
        <span> *</span>
        }
        <div [title]="helpText" class="sas-inline-block">
            <i class="bi bi-info-circle-fill sas-info-icon" aria-hidden="true"></i>
            <span [id]="field.id + 'Help'" class="visually-hidden">{{helpText}}</span>
        </div>
        }
        <div class="mb-3">
            <div class="input-group">
                    <input ngbDatepicker
                           navigation="arrows"
                           [id]="instanceId"
                           [name]="field.name"
                           class="form-control"
                           #datepicker="ngbDatepicker"
                           [formControl]="formControl"
                           placeholder="{{placeholder}}"
                           required="{{field.props.required}}"
                           [footerTemplate]="footerTemplate"
                           [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched || formSubmittedService.isSubmitted())}"
                           [readOnly]="field.props.readonly"
                           [autoClose]="'outside'"
                           outsideDays="visible"
                           tabindex="0"
                           [dayTemplate]="dayTemplate"
                           [attr.aria-invalid]="false"
                           [attr.aria-describedby]="field.id + 'Help'"
                           (dateSelect)="!field.props.readonly && onDateSelection($event); datepicker.close()"/>
                    @if (!field.props.readonly){
                    <button class="btn btn-outline-secondary bi bi-calendar" (click)="datepicker.toggle()" [disabled]="field.props.readonly" type="button">
                        <span class="visually-hidden">Toggle date picker</span>
                    </button>
                    }
                </div>
            <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
            </div>
        </div>
        </div>
        <ng-template #footerTemplate>
            <div class="dp-footer d-flex justify-content-between">
                <sas-button type="ghost" text="Today"
                            (sasOnClick)="onDateSelection(today); datepicker.close()">
                </sas-button>
                <sas-button type="ghost" text="Clear"
                            (sasOnClick)="clearDate(); datepicker.close()">
                </sas-button>
            </div>
        </ng-template>
        <ng-template #dayTemplate let-date let-currentMonth="currentMonth"
                     let-disabled="disabled">
            <div class="sas-dp-cell"
                 [class.selected]="selectedDate?.equals(date) ?? false"
                 [class.outside]="date.month !== currentMonth"
                 [class.disabled]="disabled"
                 [class.is-today]="today.equals(date)">
                {{ date.day }}
            </div>
        </ng-template>
    `
})
export class SasDatepickerFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    customFormatter = inject(CustomDateParserFormatterService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);
    private ngbCalendar = inject(NgbCalendar);

    instanceId: string = '';
    private random = Math.random().toString(36).substring(2);
    labelId: string = '';
    helpText!: string;
    placeholder!: string;
    ariaRequired: boolean = false;
    today: NgbDate = this.ngbCalendar.getToday();
    selectedDate: NgbDate | null = null;

    constructor() {
        super()
    }

    ngOnInit() {
        this.instanceId = this.field.id + this.random;

        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
            this.helpText = this.helpText.length > 0 ? `${this.helpText}. Format is dd/mm/yyyy` : 'Format is dd/mm/yyyy';
        }
        else {
            this.helpText = 'Format is dd/mm/yyyy';
        }

        this.placeholder = this.field.props['placeholder'] || SharedConstants.datePlaceholder;

        this.formControl.setValidators([
            ...(this.field.props.required ? [Validators.required] : []),
            this.dateValidator
        ]);
        const initialValue = this.customFormatter.formatToOutput(this.formControl.value);

        this.formControl.setValue(initialValue || null);
    }

    dateValidator(control: AbstractControl): { [key: string]: any } | null {
        const { value } = control;

        if (!value || typeof value !== 'string' || value.trim() === '') return null;
        function formatCorrectDate(dateString: string): string | null {
            const delimiter = dateString.includes('/') ? '/' : '-';
            const parts = dateString.split(delimiter);

            if (parts.length !== 3) {
                return null;
            }

            let day: string;
            let month: string;
            let year: string;

            if (delimiter === '/') {
                [day, month, year] = parts;
            }
            else {
                [year, month, day] = parts;
            }

            if (!day || !month || !year) {
                return null;
            }

            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }

        const formattedDate = formatCorrectDate(value);

        const datePattern1 = /^\d{2}\/\d{2}\/\d{4}$/;
        const datePattern2 = /^\d{4}-\d{2}-\d{2}$/;

        if (!formattedDate || (!datePattern1.test(formattedDate) && !datePattern2.test(value))) {
            return { 'invalidDate': true };
        }

        try {
            const [day, month, year] = formattedDate.split('/');
            const date = new Date(`${year}-${month}-${day}`);
            return isNaN(date.getTime()) ? { 'invalidDate': true } : null;
        } catch {
            return { 'invalidDate': true };
        }
    }

    onDateSelection(date: NgbDate) {
        let formattedDate = this.customFormatter.formatToOutput(date);

        if (formattedDate.includes('NaN')) {
            formattedDate = '';
        }

        this.formControl.setValue(formattedDate || null);
        this.selectedDate = date;
    }

    clearDate(): void {
        this.selectedDate = null;
        this.formControl.setValue(null);
        this.formControl.markAsPristine();
        this.formControl.markAsUntouched();
    }
}
